import { Controller } from "@hotwired/stimulus";
import { trapFocus, releaseFocusTrap } from "../utils/FocusTrap";
import { hideRailsuiModal, showRailsuiModal } from "../utils/railsui_modal";
import {
  showStudentChoiceExcludedContentModal,
  studentChoiceExcludedContentResponse,
} from "../utils/student_choice_excluded_content_modal";

export default class extends Controller {
  // Stimulus targets for modal and row elements
  static targets = [
    "row",
    "totalTime",
    "submitModal",
    "modalOpenButton",
    "searchInput",
    "documentsList",
  ];

  // On connect, set up event listeners and handlers
  connect() {
    this.handleKeydown = this.handleKeydown.bind(this);
    document.addEventListener('keydown', this.handleKeydown);
    this._attachSearchInputEnterHandler(); // Prevent Enter key from refreshing page in search input
    this._attachCheckboxKeyHandler(); // Add keydown listener for checkboxes
    this._attachSubmitModalButtonKeyHandler(); // Add keydown listener for submit modal buttons
  }

  // On disconnect, clear localStorage and remove event listeners
  disconnect() {
    localStorage.removeItem(this.localStorageKey());
    document.removeEventListener('keydown', this.handleKeydown);
    this._detachCheckboxKeyHandler();
    this._detachSubmitModalButtonKeyHandler();
  }

  // Attach keydown handler for checkboxes (for accessibility)
  _attachCheckboxKeyHandler() {
    // Delegate to document for dynamically added checkboxes
    this._checkboxKeyHandler = (event) => {
      if (
        event.target.matches &&
        event.target.matches('input.include-checkbox[data-action*="fetch-documents-by-metadata#toggleRow"]') &&
        (event.key === ' ' || event.key === 'Spacebar' || event.key === 'Enter')
      ) {
        event.preventDefault();
        event.stopPropagation();
        event.target.checked = !event.target.checked;
        // Call the Stimulus action directly so all logic runs
        if (typeof this.toggleRow === 'function') {
          // Create a synthetic event with the checkbox as target
          const syntheticEvent = { target: event.target, preventDefault: () => {}, stopPropagation: () => {} };
          this.toggleRow(syntheticEvent);
        }
      }
    };
    document.addEventListener('keydown', this._checkboxKeyHandler);
  }

  // Remove checkbox keydown handler on disconnect
  _detachCheckboxKeyHandler() {
    if (this._checkboxKeyHandler) {
      document.removeEventListener('keydown', this._checkboxKeyHandler);
    }
  }

  // Attach Enter key prevention to search input (prevents form submit)
  _attachSearchInputEnterHandler() {
    // Use event delegation on the modal to catch Enter on any search input, even if replaced dynamically
    if (this._searchInputModalHandler && this._lastModalEl) {
      this._lastModalEl.removeEventListener('keydown', this._searchInputModalHandler);
    }
    this._searchInputModalHandler = (event) => {
      if (
        event.target.classList &&
        event.target.classList.contains('fetch-documents-search-input') &&
        event.key === 'Enter'
      ) {
        event.preventDefault();
        event.stopPropagation();
        return false;
      }
    };
    const modal = document.getElementById(`fetch-documents-by-metadata-modal-content-${this.metadataType}`);
    if (modal) {
      modal.addEventListener('keydown', this._searchInputModalHandler);
      this._lastModalEl = modal;
    }
  }

  // Attach keydown handler for Add Selection and Cancel buttons in submit modal
  _attachSubmitModalButtonKeyHandler() {
    this._submitModalButtonKeyHandler = (event) => {
      // Only handle if submit modal is open
      if (!this.hasSubmitModalTarget || !this.submitModalTargets.some(m => m.classList.contains('open'))) return;
      const addBtn = document.querySelector('.fetch-documents-add-btn');
      const cancelBtn = document.querySelector('.fetch-documents-cancel-btn');
      if (!addBtn && !cancelBtn) return;
      if ((event.key === 'Enter' || event.key === ' ' || event.key === 'Spacebar')) {
        if (document.activeElement === addBtn) {
          event.preventDefault();
          addBtn.click();
        } else if (document.activeElement === cancelBtn) {
          event.preventDefault();
          cancelBtn.click();
        }
      }
    };
    document.addEventListener('keydown', this._submitModalButtonKeyHandler);
  }

  // Remove submit modal button keydown handler on disconnect
  _detachSubmitModalButtonKeyHandler() {
    if (this._submitModalButtonKeyHandler) {
      document.removeEventListener('keydown', this._submitModalButtonKeyHandler);
    }
  }

  handleKeydown(event) {
    const mainModal = document.getElementById(`fetch-documents-by-metadata-modal-content-${this.metadataType}`);
    // If submit modal is open, handle Escape for it
    if (this.hasSubmitModalTarget && this.submitModalTargets.some(m => m.classList.contains('open'))) {
      this.handleModalKeydown(event, this.submitModalTargets.find(m => m.classList.contains('open')), 'closeSubmitModal');
    } else if (mainModal && this.designSystem && event.key === 'Escape') {
      // Aquifer modal owns Escape a11y; clear injected markup after dismiss
      this.closeModal(event);
    } else if (mainModal && mainModal.style.display !== 'none' && !this.designSystem) {
      // Legacy modal: handle Escape for it
      if (event.key === 'Escape') {
        this.closeModal(event);
      }
    }
  }

  handleModalKeydown(event, modal, hideModalMethod) {
    const submitButton = modal.querySelector('input[type="submit"]');
    if (event.key === 'Enter' || event.key === ' ' || event.key === 'Spacebar') {
      // Trigger the submit button click event
      if (submitButton) submitButton.click();
    } else if (event.key === 'Escape') {
      // Trigger the cancel button click event
      this[hideModalMethod](event);
    }
  }

  // Helper to get localStorage key for this modal (unique per tab)
  localStorageKey() {
    if (!window.name) {
      window.name = `tab_${Math.random().toString(36).substr(2, 9)}`;
    }
    return `fetchDocumentsSelected_${this.metadataType}_${window.name}`;
  }

  // Get selected documents from localStorage
  getSelectedDocuments() {
    try {
      return JSON.parse(localStorage.getItem(this.localStorageKey())) || [];
    } catch {
      return [];
    }
  }

  // Restore selections from localStorage
  restoreSelectionsFromLocalStorage() {
    const selected = this.getSelectedDocuments();
    const checkboxes = document.querySelectorAll('input.include-checkbox[data-action*="fetch-documents-by-metadata#toggleRow"]');
    // Set the checked state of each checkbox based on localStorage
    checkboxes.forEach(cb => {
      const docId = cb.dataset.documentId;
      cb.checked = selected.some(item => item.id === docId);
    });
  }

  // Save current selections to localStorage
  saveSelectionsToLocalStorage(event) {
    const key = this.localStorageKey();
    const selected = this.getSelectedDocuments();
    const checked = event.target.checked;
    const docId = event.target.dataset.documentId;
    const documentTitle = event.target.dataset.documentTitle;
    const documentTime = event.target.closest('[data-document-time]')?.dataset.documentTime || event.target.dataset.documentTime;

    if (checked) {
      if (!selected.some(item => item.id === docId)) {
        selected.push({ id: docId, title: documentTitle, time: documentTime });
        localStorage.setItem(key, JSON.stringify(selected));
      }
    } else {
      const idx = selected.findIndex(item => item.id === docId);
      if (idx !== -1) {
        selected.splice(idx, 1);
        localStorage.setItem(key, JSON.stringify(selected));
      }
    }
  }

  _addButtonLabel(btn) {
    return btn.querySelector('.fetch-documents-add-btn-text') || btn.querySelector('.btn-label');
  }

  _defaultAddButtonLabel() {
    return this.designSystem ? "Add selection to course" : "Add Selection to Course";
  }

  _selectionAddButtonLabel(checkedCount) {
    if (this.designSystem) {
      return checkedCount > 0
        ? `Add ${checkedCount} selection${checkedCount > 1 ? "s" : ""} to course`
        : "Add selection to course";
    }
    return checkedCount > 0
      ? `Add ${checkedCount} Selection${checkedCount > 1 ? "s" : ""} to Course`
      : "Add Selection to Course";
  }

  // Update UI for selections (button text, time)
  updateSelectionsUI() {
    const selected = this.getSelectedDocuments();
    let totalMinutes = 0;
    selected.forEach(item => {
      const timeInSeconds = parseInt(item.time, 10) || 0;
      const timeInMinutes = Math.floor(timeInSeconds / 60);
      totalMinutes += timeInMinutes;
    });
    // Update time display
    this.updateTimeDisplay(totalMinutes);
    // Update button state/text
    const checkedCount = selected?.length || 0;
    this.modalOpenButtonTargets.forEach(btn => {
      btn.disabled = checkedCount <= 0;
      const span = this._addButtonLabel(btn);
      if (span) {
        span.textContent = this._selectionAddButtonLabel(checkedCount);
      }
    });
  }

  // Open modal and fetch content based on metadata
  openModal(event) {
    event.preventDefault();
    // Add loading class to body to force wait cursor everywhere
    document.body.classList.add('cursor-wait');
    this.metadataType = event.currentTarget.dataset.metadataType;
    this.metadata = event.currentTarget.dataset.metadata;
    this.documentIds = event.currentTarget.dataset.documentIds;
    this.designSystem = event.currentTarget.dataset.designSystem === "1";
    localStorage.removeItem(this.localStorageKey());
    // Force browser to repaint cursor before fetch
    requestAnimationFrame(() => {
      this.fetchModalContent();
    });
  }

  // Close modal and clear container
  closeModal(event) {
    event?.preventDefault?.();
    const modal = document.getElementById(`fetch-documents-by-metadata-modal-content-${this.metadataType}`);
    if (modal) {
      if (this.designSystem) {
        hideRailsuiModal(modal);
      } else {
        releaseFocusTrap(modal);
        modal.style.display = "none";
        modal.remove();
      }
    }
    const container = document.getElementById(`fetch-documents-by-metadata-modal-${this.metadataType}`);
    if (container) container.innerHTML = "";
    // Clear localStorage for this modal
    localStorage.removeItem(this.localStorageKey());
  }

  // Helper to build documents fetch URL
  buildDocumentsUrl({ search = "" } = {}) {
    const params = [
      `metadata_type=${encodeURIComponent(this.metadataType)}`,
      `metadata=${encodeURIComponent(this.metadata)}`,
      `document_ids=${encodeURIComponent(this.documentIds)}`,
      `format=turbo_stream`,
    ];

    if (this.designSystem) params.push("design_system=1");
    if (search) params.push(`search=${encodeURIComponent(search)}`);

    return `/document_sets/fetch_documents_by_metadata?${params.join("&")}`;
  }

  // Fetch modal content via AJAX and inject into container
  fetchModalContent() {
    const url = this.buildDocumentsUrl();
    fetch(url, {
      headers: { 'Accept': 'text/vnd.turbo-stream.html' }
    })
      .then(response => response.text())
      .then(async (html) => {
        const container = document.getElementById(`fetch-documents-by-metadata-modal-${this.metadataType}`);
        if (container) {
          container.innerHTML = html;
          const modal = container.querySelector(`#fetch-documents-by-metadata-modal-content-${this.metadataType}`);
          if (modal) {
            if (this.designSystem) {
              await showRailsuiModal(modal);
            } else {
              modal.style.display = "flex";
              trapFocus(modal); // Trap focus within the modal content only
            }
          }
          // Update button state
          const modalOpenButtons = container.querySelectorAll('[data-fetch-documents-by-metadata-target="modalOpenButton"]');
          modalOpenButtons.forEach(btn => {
            btn.disabled = true;
            const span = this._addButtonLabel(btn);
            if (span) {
              span.textContent = this._defaultAddButtonLabel();
            }
          });
          // Restore selections and update UI only after modal loads
          this.restoreSelectionsFromLocalStorage();
          this.updateSelectionsUI();
          container.dispatchEvent(new Event('card:content:loaded', { bubbles: true }));
          // Re-attach Enter key prevention in case search input was re-rendered
          this._attachSearchInputEnterHandler();
        }
        // Always remove loading class after loading
        document.body.classList.remove('cursor-wait');
      })
      .catch(() => {
        document.body.classList.remove('cursor-wait');
      });
  }

  // Toggle row selection, update localStorage and UI
  toggleRow(event) {
    this.saveSelectionsToLocalStorage(event);
    this.updateSelectionsUI();
  }

  // Debounce helper (for search input)
  debounce(func, wait) {
    let timeout;
    return function(...args) {
      const later = () => {
        timeout = null;
        func.apply(this, args);
      };
      clearTimeout(timeout);
      timeout = setTimeout(later, wait);
    };
  }

  // AJAX search for documents and update modal content (debounced)
  search(event) {
    // Always store the latest value
    this._latestQuery = event.target.value;
    if (!this._debouncedSearch) {
      this._debouncedSearch = this.debounce(
        (query) => this._searchDocuments(query),
        300
      );
    }
    this._debouncedSearch(this._latestQuery);
  }

  _searchDocuments(query) {
    const url = this.buildDocumentsUrl({ search: query });
    // Use a request counter to ensure only the latest response updates the UI
    if (!this._searchRequestCounter) this._searchRequestCounter = 0;
    const currentRequest = ++this._searchRequestCounter;

    fetch(url, {
      headers: { Accept: "text/vnd.turbo-stream.html" },
    })
      .then((response) => response.text())
      .then((html) => {
        // Only update UI if this is the latest request
        if (currentRequest === this._searchRequestCounter) {
          const documentsList = this.documentsListTarget;
          if (documentsList) {
            // Try to extract the first element with the same target as documentsList
            const tempDiv = document.createElement("div");
            tempDiv.innerHTML = html;
            // Try to find the element with the same data-target or class as documentsList
            let newContent = null;
            // Prefer data-fetch-documents-by-metadata-target attribute
            if (documentsList.dataset.fetchDocumentsByMetadataTarget) {
              newContent = tempDiv.querySelector(
                `[data-fetch-documents-by-metadata-target="${documentsList.dataset.fetchDocumentsByMetadataTarget}"]`
              );
            }
            // Fallback to same class name if not found
            if (!newContent && documentsList.className) {
              newContent = tempDiv.querySelector(
                `.${documentsList.className.split(" ").join(".")}`
              );
            }
            // Fallback to all content
            documentsList.innerHTML = newContent ? newContent.innerHTML : html;
            this.restoreSelectionsFromLocalStorage();
            this.updateSelectionsUI();
          }
        }
      })
      .catch((error) => {
        // Log errors but do not block UI
        console.error("Search fetch error:", error);
      });
  }

  // Update the time display in modal
  updateTimeDisplay(totalMinutes) {
    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;
    if (this.hasTotalTimeTarget && this.totalTimeTarget.dataset.format === "figma") {
      const padded = String(minutes).padStart(2, "0");
      this.totalTimeTarget.textContent = `Additional Time: ${hours}h ${padded}min`;
      return;
    }
    let timeString = "";
    if (hours > 0) {
      timeString = `${hours} hour${hours !== 1 ? "s" : ""}`;
      if (minutes > 0) {
        timeString += ` ${minutes} minute${minutes !== 1 ? "s" : ""}`;
      }
    } else {
      timeString = `${minutes} minute${minutes !== 1 ? "s" : ""}`;
    }
    this.totalTimeTarget.textContent = `Additional ${timeString}`;
  }

  // Update modal button state based on checked rows
  updateModalButtonState() {
    const checkedCount = this.rowTargets.filter(
      (row) => row?.querySelector(".include-checkbox")?.checked
    ).length;
    this.modalOpenButtonTargets.forEach(btn => {
      btn.disabled = checkedCount === 0;
      const span = this._addButtonLabel(btn);
      if (span) {
        span.textContent = this._selectionAddButtonLabel(checkedCount);
      }
    });
  }

  // Open submit modal, print selected items, and cache selected IDs
  openSubmitModal() {
    // Cache selected document IDs for later use
    this.selectedDocumentIds = this.getSelectedDocuments()?.map(item => item.id);
    this.submitModalTargets.forEach(modal => {
      modal.classList.remove("hidden");
      modal.classList.add("open");
      this.printSelectedItems(modal);
      // Trap focus for accessibility
      trapFocus(modal);
    });
    // Close open modal before opening submit modal
    const modal = document.getElementById(`fetch-documents-by-metadata-modal-content-${this.metadataType}`);
    if (modal) {
      modal.style.display = "none";
      modal.remove();
    }
  }

  // Print selected items in submit modal
  printSelectedItems(modal) {
    const selected = this.getSelectedDocuments();
    // Append the selected items to cases container in the submitModal in p tag
    const casesContainer = modal.querySelector("[data-fetch-documents-by-metadata-target='cases']");
    if (casesContainer) {
      if (selected.length > 0) {
        casesContainer.innerHTML = selected
          .map((item) => `<p>${item.title}</p>`)
          .join("");
      } else {
        const selectedItems = this.rowTargets
          .filter((row) => row?.querySelector(".include-checkbox")?.checked)
          .map((row) => row?.dataset?.documentTitle);
        casesContainer.innerHTML = selectedItems
          .map((item) => `<p>${item}</p>`)
          .join("");
      }
    }
  }

  // Close submit modal
  closeSubmitModal() {
    this.submitModalTargets.forEach(modal => {
      modal.classList.remove("open");
      modal.classList.add("hidden");
    });
  }

  // Submit checked documents to API using cached IDs
  submitChecked() {
    const selectedDocumentIds =
      (this.selectedDocumentIds && this.selectedDocumentIds.length > 0
        ? this.selectedDocumentIds
        : this.getSelectedDocuments()?.map((item) => item.id)) || [];
    if (!selectedDocumentIds.length) {
      return;
    }
    const pathname = window.location.pathname;
    const params = new URLSearchParams(window.location.search);
    // Check for new_course_builder in URL
    if (pathname.includes('new_course_builder')) {
      // Get selection_basket_id from params
      const selectionBasketId = params.get('selection_basket_id');
      if (!selectionBasketId) {
        alert('Selection basket ID not found.');
        return;
      }
      // Prepare selection_basket_items array
      const selectionBasketItems = selectedDocumentIds.map(id => ({ selectable_class: 'Document', selectable_id: id }));
      // Add a flag to indicate request source
      const payload = {
        selection_basket_items: selectionBasketItems,
        selection_basket_id: selectionBasketId,
        from_metadata_modal: true
      };
      fetch(`/selection_baskets/${selectionBasketId}/selection_basket_items`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': document.querySelector("meta[name='csrf-token']").content,
        },
        body: JSON.stringify(payload)
      })
        .then(response => {
          if (!response.ok) throw new Error('API call failed');
          return response.json();
        })
        .then(() => {
          // Build new URL with added document_ids
          selectedDocumentIds.forEach(id => {
            params.append('document_ids[]', id);
          });
          Turbo.visit(pathname + '?' + params.toString());
        })
        .catch(error => {
          alert('Something went wrong. Please try again.');
        });
    } else {
      // Fallback to legacy document_set relation
      let documentSetId = null;
      const pathMatch = pathname.match(/document_sets\/(\d+)/);
      if (pathMatch && pathMatch[1]) {
        documentSetId = pathMatch[1];
      }
      fetch(`/document_sets/${documentSetId}/document_set_document_relations/create_batch_list`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
          "X-CSRF-Token": document.querySelector("meta[name='csrf-token']").content,
        },
        body: JSON.stringify({ documentIds: selectedDocumentIds }),
      })
        .then(async (response) => {
          const text = await response.text();
          const data = text.trim() ? JSON.parse(text) : {};
          if (studentChoiceExcludedContentResponse(response, data)) {
            showStudentChoiceExcludedContentModal();
            return null;
          }
          if (!response.ok) throw new Error("API call failed");
          return data;
        })
        .then((data) => {
          if (data === null) return;
          Turbo.visit(window.location.href);
        })
        .catch((error) => {
          alert("Something went wrong. Please try again.");
        });
    }
    this.closeSubmitModal();
    if (this.designSystem) {
      this.closeModal({ preventDefault() {} });
    }
  }
}
